import React from "react";

const MethodologyExclusions: React.FC = () => (
  <div className="space-y-6 text-sm md:text-base leading-relaxed text-[#380C1E]/90 font-light">
    <section>
      <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
        6. Exclusions
      </h2>
      <p className="mb-3">
        The instrument's credibility depends as much on what it refuses to do as
        on what it does. The following are explicit exclusions.
      </p>
      <p className="mb-3">
        <strong>Not a diagnosis.</strong> The quiz does not diagnose burnout,
        depression, anxiety, or any other condition, and it does not produce a
        score that can be compared against a clinical threshold. No cutoff in
        this document should be read as one.
      </p>
      <p className="mb-3">
        <strong>Not a depression screen.</strong> Given the documented overlap
        between burnout and depression (Bianchi, Schonfeld, and Laurent, 2015),
        the instrument does not attempt to distinguish the two. Its care note
        names the distinction the framework draws in practice, coaching for
        burnout and licensed therapy for depression, and directs respondents who
        feel hopeless, numb, or unable to function to a doctor or licensed
        counselor. The care flag (Section 5.8) is a placement rule for that
        note, not a screening result.
      </p>
      <p className="mb-3">
        <strong>Not an occupational burnout measure.</strong> The instrument
        deliberately does not restrict itself to the workplace and therefore
        does not measure ICD-11 QD85 burn-out as the WHO defines it. Readers
        looking for an occupational measure should use the MBI, the OLBI, or the
        BAT.
      </p>
      <p className="mb-3">
        <strong>Not a personality or trait measure.</strong> Every item is
        anchored to "lately" and "right now." The instrument is designed to
        describe a season, and a respondent's profile is expected to change as
        her circumstances change.
      </p>
      <p className="mb-3">
        <strong>Excluded content.</strong> The item bank contains no items about
        trauma history, abuse, substance use, self-harm, or suicidal thinking.
        This is deliberate. A three-minute unsupervised quiz is not an
        appropriate place to elicit those disclosures, and the instrument does
        not pretend to respond to them.
      </p>
      <p className="mb-3">
        <strong>Excluded populations.</strong> The instrument was written for
        adult women, with language drawn from the lives of Christian women who
        carry roles at home, at work, and in church. It has not been written
        for, tested with, or adapted to men, adolescents, or respondents outside
        that cultural and faith context. It is not offered to minors.
      </p>
      <p>
        <strong>The unscored item.</strong> Screen 33 (body, emotions, spirit)
        is excluded from all scoring. It personalizes one paragraph of the
        report and nothing else.
      </p>
    </section>

    <section>
      <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
        7. Assumptions
      </h2>
      <p className="mb-3">
        The scoring in Section 5 is only as sound as the assumptions beneath it.
        They are listed here so they can be tested rather than taken on faith.
      </p>
      <p className="mb-3">
        <strong>Equal item weight.</strong> Every choice screen contributes
        exactly one point. No item is assumed to be more diagnostic than
        another. This is the simplest defensible choice in the absence of data,
        and it is the first assumption a validation study should test.
      </p>
      <p className="mb-3">
        <strong>Domain balance.</strong> Because each domain appears in exactly
        10 screens against every other domain exactly twice, the design assumes
        that no domain has an unfair number of opportunities to be chosen. That
        balance is a property of the design and was verified by count.
      </p>
      <p className="mb-3">
        <strong>Weight matching within pairs.</strong> The design assumes the
        two statements in each pair are roughly equally comfortable to admit.
        This was pursued by writing and review, not measured. If a pair is
        unbalanced, the safer statement's domain will be over-selected.
      </p>
      <p className="mb-3">
        <strong>Honest, first-instinct responding.</strong> The instrument
        assumes the respondent answers about her own life, lately, most of the
        time, and without deliberating. There is no lie scale and no consistency
        check beyond the two-stem convergence indicator.
      </p>
      <p className="mb-3">
        <strong>Shared reference period.</strong> All items assume the
        respondent applies the same window ("lately") throughout. The intensity
        block then asks about duration separately.
      </p>
      <p className="mb-3">
        <strong>Exhaustiveness of the six domains.</strong> The design assumes
        that the source of a respondent's burnout falls within the six B's. A
        respondent whose actual source lies outside them will still receive a
        ranked profile, because forced choice always produces one. That profile
        would be an artifact of the design, not a finding.
      </p>
      <p className="mb-3">
        <strong>Additive severity.</strong> Duration and impact are assumed to
        contribute equally and additively to severity, and the Early-Stage/Deep
        boundary between 4 and 5 is a judgment, not a derived threshold.
      </p>
      <p className="mb-3">
        <strong>Language and reading.</strong> The instrument assumes fluency in
        the American English idiom its statements are written in (for example,
        "the car line," "the group chat"). It has not been translated or
        adapted.
      </p>
      <p>
        <strong>Faith content does not affect scoring.</strong> The report uses
        scripture and ministry language. None of that language is present in the
        items, and none of it enters the scoring. A respondent who does not
        share the author's faith receives the same profile from the same
        answers; only the interpretive text around it is faith-based.
      </p>
    </section>

    <section>
      <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
        8. Care language and referral
      </h2>
      <p className="mb-3">
        Every full report carries the following note verbatim: "This quiz is a
        reflection tool, not a medical or psychological diagnosis. Ieshia will
        tell you this herself: coaching is for burnout, and licensed therapists
        are for depression. If you've been feeling hopeless, numb, or unable to
        function, please also talk to a doctor or licensed counselor. That's not
        weakness. That's stewardship of you."
      </p>
      <p>
        Two referral rules are built into the framework and appear in the report
        where relevant. First, when the impact item is answered at its highest
        level, the care note is moved to the top of the report as a highlighted
        card. Second, when Belonging is the primary domain, the report advises
        the respondent to work with a licensed therapist alongside any coaching
        program, because in the author's practice this domain most often traces
        to a relational wound that coaching is not designed to treat.
      </p>
    </section>

    <section>
      <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
        9. Data handling
      </h2>
      <p>
        No personal information is collected before or during the 33 screens.
        The primary result and its explanation are shown without any form. A
        respondent who wants the full ranked report provides a first name and
        email address, after which the full report is displayed and emailed.
        Stored fields are the six domain percentages, the primary and runner-up
        domains, the severity score, the unscored presentation item, the care
        flag, the instrument version, and the date taken. A respondent who
        retakes the quiz overwrites her previous scores; earlier version tags
        are kept for record-keeping only. Data handling is governed by the
        site's{" "}
        <a
          href="https://iamieshiadanielle.com/privacy"
          className="text-[#9E1B50] underline"
        >
          privacy policy
        </a>
        .
      </p>
    </section>
  </div>
);

export default MethodologyExclusions;
